import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  TextField,
  Button,
  Skeleton,
  Chip,
  Switch,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AttendanceDTO, BulkAttendanceInput, TermDTO } from '@school-homestead/shared'
import { SESSION_LABELS } from '@school-homestead/shared'
import type { Session } from '@school-homestead/shared'

/** Returns today's date as YYYY-MM-DD in local time. */
function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

interface AttendanceRow {
  enrollmentId: number
  studentName: string
  studentId: number
  present: boolean
  notes: string
}

export default function RollCallPage() {
  const queryClient = useQueryClient()
  const [sessionTab, setSessionTab] = useState<Session>('mon_wed')
  const [date, setDate] = useState(todayLocal())
  const [rows, setRows] = useState<AttendanceRow[]>([])
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Load active term
  const { data: activeTerm } = useQuery({
    queryKey: ['terms', 'active'],
    queryFn: async () => {
      const res = await api.get<TermDTO | null>('/api/terms/active')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  // Load attendance for date + session + term
  const { data, isLoading, error } = useQuery({
    queryKey: ['attendance', activeTerm?.id, date, sessionTab],
    queryFn: async () => {
      if (!activeTerm) return []
      const res = await api.get<AttendanceDTO[]>(
        `/api/attendance?date=${date}&session=${sessionTab}&termId=${activeTerm.id}`,
      )
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    enabled: !!activeTerm,
  })

  // Sync fetched data into local editable state.
  // This is the legitimate "subscribe for updates from an external system" pattern:
  // react-query provides server state, and we sync it into local editable state.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (data) {
      setRows(
        data.map((d) => ({
          enrollmentId: d.enrollmentId,
          studentName: d.studentName ?? 'Unknown',
          studentId: d.studentId ?? 0,
          present: d.present,
          notes: d.notes ?? '',
        })),
      )
    }
  }, [data])
  /* eslint-enable react-hooks/set-state-in-effect */

  const saveMutation = useMutation({
    mutationFn: async (input: BulkAttendanceInput) => {
      const res = await api.post<AttendanceDTO[]>('/api/attendance/bulk', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
      setSaveSuccess(true)
    },
  })

  function handleToggle(enrollmentId: number) {
    setRows((prev) =>
      prev.map((r) => (r.enrollmentId === enrollmentId ? { ...r, present: !r.present } : r)),
    )
  }

  function handleNotes(enrollmentId: number, notes: string) {
    setRows((prev) =>
      prev.map((r) => (r.enrollmentId === enrollmentId ? { ...r, notes } : r)),
    )
  }

  function handleSave() {
    saveMutation.mutate({
      date,
      session: sessionTab,
      records: rows.map((r) => ({
        enrollmentId: r.enrollmentId,
        present: r.present,
        notes: r.notes || undefined,
      })),
    })
  }

  const presentCount = rows.filter((r) => r.present).length
  const absentCount = rows.filter((r) => !r.present).length

  if (!activeTerm) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Roll Call
        </Typography>
        <Alert severity="info">
          No active term set. Please contact an administrator to activate a term.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Roll Call{' '}
          {!isLoading && rows.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
              ({rows.length})
            </Typography>
          )}
        </Typography>
        <Chip label={activeTerm.name} color="primary" size="small" />
      </Box>

      {/* Date picker + Session tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          label="Date"
        />
      </Box>

      <Tabs
        value={sessionTab}
        onChange={(_, v) => setSessionTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label={SESSION_LABELS.mon_wed} value="mon_wed" />
        <Tab label={SESSION_LABELS.tue_thu} value="tue_thu" />
      </Tabs>

      {/* Summary */}
      {rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${presentCount} present`}
            color="success"
            variant="outlined"
            size="small"
          />
          <Chip
            icon={<CancelIcon />}
            label={`${absentCount} absent`}
            color="error"
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load attendance.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120 }} align="center">
                Present
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    {[1, 2, 3].map((j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={row.enrollmentId}
                    hover
                    sx={{
                      borderLeft: 4,
                      borderColor: row.present ? 'success.main' : 'error.main',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.studentName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={row.present}
                        onChange={() => handleToggle(row.enrollmentId)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Notes for today..."
                        value={row.notes}
                        onChange={(e) => handleNotes(row.enrollmentId, e.target.value)}
                        fullWidth
                        size="small"
                        variant="standard"
                      />
                    </TableCell>
                  </TableRow>
                ))}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No enrolled students for {SESSION_LABELS[sessionTab]} in {activeTerm.name}.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save button */}
      {rows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saveMutation.isPending}
            size="large"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Attendance'}
          </Button>
        </Box>
      )}

      {saveMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(saveMutation.error as Error).message}
        </Alert>
      )}

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        message="Attendance saved successfully"
      />
    </Box>
  )
}
